import * as React from 'react';
import classnames from 'classnames';
import { isEmpty, isNil } from 'lodash';

import Spinner from 'components/spinner/Spinner';
import Popover from 'components/popover/Popover';
import EmptyMessage from 'modules/reporting/components/empty-message/EmptyMessage';

import { __t, applyTranslations } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';

import type { BarChartProgressItem, BarChartProgressMetricContainerWidth, BarChartStyle } from 'types';

import './bar-chart-progress.scss';

applyTranslations(require('./bar-chart-progress.i18n.json'));

type BarChartTooltip = {
  title: string,
  text: string
};

type TitleTooltip = Pick<BarChartTooltip, 'text'>;

type Props = {
  barStyle: BarChartStyle,
  title: string,
  titleTooltip?: TitleTooltip,
  tooltip?: BarChartTooltip,
  data: Array<BarChartProgressItem>,
  emptyMessage?: string,
  padded: boolean,
  hasMinHeight?: boolean,
  emptyMessageWrapperClass?: string,
  isLoading: boolean,
  isMetricAlignmentLeft?: boolean,
  metricContainerType?: BarChartProgressMetricContainerWidth
};

export const getMetricStr = (barChartItem: BarChartProgressItem) => (
  !isNil(barChartItem.estimated.metric) ? `${barChartItem.actual.metric}/${barChartItem.estimated.metric}` : barChartItem.actual.metric
);

class BarChartProgress extends React.Component<Props> {
  static defaultProps = {
    padded: false,
    barStyle: {},
    hasMinHeight: true
  };

  getPercentage = (percentage: number): string => `${percentage}%`;

  renderSpinner(): JSX.Element {
    return (
      <div className="bar-chart-progress__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderEmpty(): JSX.Element {
    const { emptyMessage, emptyMessageWrapperClass } = this.props;

    return (
      <EmptyMessage
        message={emptyMessage}
        wrapperClass={emptyMessageWrapperClass}
      />
    );
  }

  renderHorizontalChart(item: BarChartProgressItem): React.ReactNode {
    const { barStyle, tooltip, isMetricAlignmentLeft, metricContainerType } = this.props;
    let background = item.bgColor;

    if (item.complementaryColors && item.complementaryColors.length > 0) {
      const gradientAdditions = item.complementaryColors
        .map((color, index) =>
          `${color} ${25 * (index + 1) + 1}px,${color} ${25 * (index + 2)}px`
        )
        .join(',');
      background = `repeating-linear-gradient(116deg,${item.bgColor},${item.bgColor} 25px,${gradientAdditions})`;
    }

    const metricContainerClasses = classnames({
      'bar-chart-progress__metric-container': true,
      'bar-chart-progress__metric-container--align-left': isMetricAlignmentLeft,
      [`bar-chart-progress__metric-container--${metricContainerType}`]: metricContainerType
    });

    const metric = (
      <div className={metricContainerClasses}>
        <div className="bar-chart-progress__metric">
          {getMetricStr(item)}
        </div>
        <div className="bar-chart-progress__info">
          {item.info}
        </div>
      </div>
    );

    const barChartContainerStyles = item.width ?
      { borderColor: item.bgColor,
        width: item.width,
        flex: 'none'
      } : { borderColor: item.bgColor };
    const actualWidth = this.getPercentage(item.actual.percentage || 0);

    return (
      <div
        key={`${item.key}-${item.bgColor}`}
        className="bar-chart-progress__container"
        style={{
          marginBottom: `${barStyle?.container?.marginBottom ? String(barStyle.container.marginBottom) : 26}px`,
          minHeight: barStyle?.container?.minHeight ? `${String(barStyle.container.minHeight)}px` : 'initial'
        }}
      >
        {this.renderLabel(item)}
        <div
          className="bar-chart-progress__bar-container"
          style={barChartContainerStyles}
        >
          <div
            className="bar-chart-progress__bar"
            style={{
              width: actualWidth,
              background,
              height: barStyle.height
            }}
          />
        </div>
        {
          tooltip
            ? this.renderTooltip(tooltip, metric)
            : metric
        }
      </div>
    );
  }

  renderTooltip = (tooltip: BarChartTooltip, triggerComponent: React.ReactNode): JSX.Element => (
    <Popover
      classNamePopover="bar-chart-progress__popover"
      triggerComponent={triggerComponent}
    >
      <div className="bar-chart-progress__popover-title">
        {tooltip.title}
      </div>
      <div
        className="bar-chart-progress__popover-text"
        dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(tooltip.text) }}
      />
    </Popover>
  );

  renderTitle() {
    const { barStyle, title, titleTooltip } = this.props;

    return (
      <div
        className="bar-chart-progress__title"
        style={{
          marginBottom: `${barStyle.title ? String(barStyle.title.marginBottom) : 26}px`
        }}
      >
        {title}
        {titleTooltip ? (
          <div className="bar-chart-progress__title-popover-wrapper">
            <Popover
              showQuestionMark
              classNamePopover="bar-chart-progress__title-popover"
            >
              <div
                className="bar-chart-progress__title-popover-text"
                dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(titleTooltip.text) }}
              />
            </Popover>
          </div>
        ) : null}
      </div>
    );
  }

  renderLabel = (item: BarChartProgressItem): JSX.Element => {
    const { barStyle } = this.props;
    let barChartLabelStyle: React.CSSProperties = {
      marginTop: `${barStyle.label ? String(barStyle.label.marginTop) : 0}px`
    };

    barChartLabelStyle = barStyle.label?.width ? {
      ...barChartLabelStyle,
      width: `${barStyle.label.width}px`
    } : barChartLabelStyle;

    return (
      <div
        className="bar-chart-progress__label"
        key={item.label}
        style={barChartLabelStyle}
      >
        {item.label}
        <div className="bar-chart-progress__progress-label">
          {Boolean(item.estimated.metric) && __t('bar_chart_progress.progress_label')}
        </div>
      </div>
    );
  };

  renderChart(): React.ReactNode {
    const { data, isLoading } = this.props;
    const noData = isEmpty(data);

    if (isLoading) {
      return this.renderSpinner();
    }

    if (noData) {
      return this.renderEmpty();
    }

    return data.map(item => this.renderHorizontalChart(item));
  }

  render(): JSX.Element {
    const { padded, hasMinHeight } = this.props;

    const contentClasses = classnames({
      'bar-chart-progress__content': true,
      'bar-chart-progress__content--padded': padded,
      'bar-chart-progress__content--no-min-height': !hasMinHeight
    });

    return (
      <section className="bar-chart-progress">
        { this.renderTitle() }
        <div className={contentClasses}>
          <div className="bar-chart-progress__bars">
            {this.renderChart()}
          </div>
        </div>
      </section>
    );
  }
}

export default BarChartProgress;
